import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Sparkles, Heart } from 'lucide-react';

/**
 * MuscleMap — SVG body silhouette that colour-codes fine-grained muscle
 * regions by their recent engagement (sets × reps × weight, normalised
 * 0..1 against the most-worked muscle in the period).
 *
 * Fine-grained regions (per the backend /api/insights/muscle-engagement
 * `side` field):
 *
 *   Front view (anterior):
 *     - chest, front_delts, side_delts
 *     - biceps (L/R), forearms
 *     - abs, obliques
 *     - quads (L/R), calves
 *
 *   Back view (posterior):
 *     - traps, lats (L/R), lower_back
 *     - rear_delts
 *     - triceps (L/R), forearms
 *     - glutes, hamstrings (L/R), calves
 *
 * Each region has a region-coded colour that interpolates through a
 * 4-stop heat gradient (cool idle → warm → hot → inferno) so the eye
 * can pick out heavy vs light vs untouched muscles at a glance, not
 * just "lime more or less".
 *
 * Props:
 *   engagement     — array of { key, side, intensity, volume, sets,
 *                            exercises, last_trained_at } from the
 *                            backend. Optional; if missing, all
 *                            regions render at intensity 0.
 *   onMuscleClick  — optional (groupKey) => void
 *   compact        — boolean, default false
 *   height         — number, default 460
 */
export const MuscleMap = ({
  engagement = [],
  onMuscleClick,
  compact = false,
  height = 460,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState('front');
  const [hovered, setHovered] = useState(null);

  const lookup = useMemo(() => {
    const m = {};
    for (const g of engagement) m[g.key] = g;
    return m;
  }, [engagement]);

  const getIntensity = (key) => lookup?.[key]?.intensity ?? 0;
  const getStats = (key) => lookup?.[key];

  const labels = {
    chest: t('muscleMap.muscle.chest', { defaultValue: 'Chest' }),
    front_delts: t('muscleMap.muscle.front_delts', { defaultValue: 'Front delts' }),
    side_delts: t('muscleMap.muscle.side_delts', { defaultValue: 'Side delts' }),
    rear_delts: t('muscleMap.muscle.rear_delts', { defaultValue: 'Rear delts' }),
    biceps: t('muscleMap.muscle.biceps', { defaultValue: 'Biceps' }),
    triceps: t('muscleMap.muscle.triceps', { defaultValue: 'Triceps' }),
    forearms: t('muscleMap.muscle.forearms', { defaultValue: 'Forearms' }),
    traps: t('muscleMap.muscle.traps', { defaultValue: 'Traps' }),
    lats: t('muscleMap.muscle.lats', { defaultValue: 'Lats' }),
    lower_back: t('muscleMap.muscle.lower_back', { defaultValue: 'Lower back' }),
    abs: t('muscleMap.muscle.abs', { defaultValue: 'Abs' }),
    obliques: t('muscleMap.muscle.obliques', { defaultValue: 'Obliques' }),
    quads: t('muscleMap.muscle.quads', { defaultValue: 'Quads' }),
    hamstrings: t('muscleMap.muscle.hamstrings', { defaultValue: 'Hamstrings' }),
    glutes: t('muscleMap.muscle.glutes', { defaultValue: 'Glutes' }),
    calves: t('muscleMap.muscle.calves', { defaultValue: 'Calves' }),
    cardio: t('muscleMap.muscle.cardio', { defaultValue: 'Cardio' }),
  };

  const handle = (key) => (e) => {
    setHovered(key);
    onMuscleClick?.(key, getStats(key));
    if (e?.preventDefault) e.preventDefault();
  };

  return (
    <div
      className={`relative ${compact ? '' : 'p-6'}`}
      data-testid="muscle-map"
      data-view={view}
    >
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="hero-kicker">
              {t('muscleMap.kicker', { defaultValue: 'Body map' })}
            </span>
            <h3 className="mt-1 font-display text-xl uppercase text-[var(--color-text)]">
              {t('muscleMap.title', { defaultValue: 'What you trained' })}
            </h3>
          </div>
          <ViewToggle view={view} setView={setView} t={t} />
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Figure */}
        <div
          className="relative shrink-0 mx-auto"
          style={{ height, width: height * 0.6 }}
        >
          <svg
            key={view}
            viewBox="0 0 200 540"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full absolute inset-0 transition-opacity duration-200"
            data-testid={`muscle-map-${view}`}
          >
            <defs>
              {/* Glow filter for hot regions (intensity ≥ 0.7) */}
              <filter id="muscle-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.6" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Stronger glow for inferno regions (intensity ≥ 0.9) */}
              <filter id="muscle-glow-hot" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <radialGradient id="body-fill" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#0d1512" />
                <stop offset="100%" stopColor="#070b08" />
              </radialGradient>
            </defs>

            <BodySilhouette />

            {view === 'front' ? (
              <FrontView
                labels={labels}
                getIntensity={getIntensity}
                hovered={hovered}
                setHovered={setHovered}
                handle={handle}
                compact={compact}
              />
            ) : (
              <BackView
                labels={labels}
                getIntensity={getIntensity}
                hovered={hovered}
                setHovered={setHovered}
                handle={handle}
                compact={compact}
              />
            )}

            {/* View hint */}
            <text
              x="100"
              y="530"
              textAnchor="middle"
              className="fill-[var(--color-text-muted)] font-display"
              style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase' }}
            >
              {view === 'front' ? 'Anterior' : 'Posterior'}
            </text>
          </svg>
        </div>

        {!compact && (
          <div className="flex-1 min-w-0 space-y-3 w-full">
            <CardioStat stats={lookup?.cardio} t={t} />
            <MuscleList
              groups={view === 'front'
                ? ['chest', 'front_delts', 'side_delts', 'biceps', 'forearms', 'abs', 'obliques', 'quads', 'calves']
                : ['traps', 'lats', 'lower_back', 'rear_delts', 'triceps', 'forearms', 'glutes', 'hamstrings', 'calves']
              }
              labels={labels}
              lookup={lookup}
              hovered={hovered}
              setHovered={setHovered}
              onClick={onMuscleClick}
              t={t}
            />
            <ColorScale t={t} />
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// Body silhouette
// ===========================================================================
function BodySilhouette() {
  return (
    <g className="body-silhouette">
      {/* Head */}
      <ellipse cx="100" cy="28" rx="20" ry="26" fill="url(#body-fill)" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      {/* Neck */}
      <rect x="90" y="50" width="20" height="20" fill="url(#body-fill)" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      {/* Torso */}
      <path
        d="M 55 90 Q 50 86 50 100 L 48 160 Q 50 200 60 245 L 65 285 L 78 295 L 122 295 L 135 285 L 140 245 Q 150 200 152 160 L 150 100 Q 150 86 145 90 L 128 72 L 72 72 Z"
        fill="url(#body-fill)"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth="1.2"
      />
      {/* Arms */}
      <path
        d="M 48 92 Q 38 100 35 130 Q 33 170 40 210 Q 48 225 58 220 Q 55 195 55 165 Q 55 130 60 100 Z"
        fill="url(#body-fill)"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="1"
      />
      <path
        d="M 152 92 Q 162 100 165 130 Q 167 170 160 210 Q 152 225 142 220 Q 145 195 145 165 Q 145 130 140 100 Z"
        fill="url(#body-fill)"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="1"
      />
      {/* Legs */}
      <path
        d="M 78 295 L 72 360 Q 68 415 70 460 Q 75 490 90 490 Q 95 460 95 415 L 95 295 Z"
        fill="url(#body-fill)"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="1"
      />
      <path
        d="M 122 295 L 128 360 Q 132 415 130 460 Q 125 490 110 490 Q 105 460 105 415 L 105 295 Z"
        fill="url(#body-fill)"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="1"
      />
    </g>
  );
}

// ===========================================================================
// Front view
// ===========================================================================
function FrontView({ labels, getIntensity, hovered, setHovered, handle, compact }) {
  return (
    <g data-testid="muscle-map-front-regions">
      {/* Side delts (outer shoulder caps) */}
      <Muscle
        d="M 50 100 Q 38 102 35 118 Q 32 135 42 145 Q 50 142 55 130 L 60 105 Z"
        group="side_delts"
        label={labels.side_delts}
        intensity={getIntensity('side_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('side_delts')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 150 100 Q 162 102 165 118 Q 168 135 158 145 Q 150 142 145 130 L 140 105 Z"
        group="side_delts"
        label={labels.side_delts}
        intensity={getIntensity('side_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('side_delts')}
        variant="right"
        compact={compact}
      />

      {/* Front delts (front of shoulder) */}
      <Muscle
        d="M 60 95 Q 55 100 60 115 Q 70 118 80 112 L 80 92 Z"
        group="front_delts"
        label={labels.front_delts}
        intensity={getIntensity('front_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('front_delts')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 140 95 Q 145 100 140 115 Q 130 118 120 112 L 120 92 Z"
        group="front_delts"
        label={labels.front_delts}
        intensity={getIntensity('front_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('front_delts')}
        variant="right"
        compact={compact}
      />

      {/* Chest */}
      <Muscle
        d="M 78 115 Q 70 120 70 145 Q 75 175 100 175 Q 125 175 130 145 Q 130 120 122 115 Q 110 122 100 122 Q 90 122 78 115 Z"
        group="chest"
        label={labels.chest}
        intensity={getIntensity('chest')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('chest')}
        compact={compact}
      />

      {/* Biceps (front of upper arm) */}
      <Muscle
        d="M 42 130 Q 38 160 45 195 Q 55 198 60 188 Q 60 160 60 135 Z"
        group="biceps"
        label={labels.biceps}
        intensity={getIntensity('biceps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('biceps')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 158 130 Q 162 160 155 195 Q 145 198 140 188 Q 140 160 140 135 Z"
        group="biceps"
        label={labels.biceps}
        intensity={getIntensity('biceps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('biceps')}
        variant="right"
        compact={compact}
      />

      {/* Forearms (lower arm, just above wrist) */}
      <Muscle
        d="M 44 200 Q 42 215 48 222 Q 58 220 60 210 Q 60 200 58 196 Z"
        group="forearms"
        label={labels.forearms}
        intensity={getIntensity('forearms')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('forearms')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 156 200 Q 158 215 152 222 Q 142 220 140 210 Q 140 200 142 196 Z"
        group="forearms"
        label={labels.forearms}
        intensity={getIntensity('forearms')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('forearms')}
        variant="right"
        compact={compact}
      />

      {/* Abs (centre rect) */}
      <Muscle
        d="M 88 180 L 112 180 L 110 240 Q 100 245 90 240 Z"
        group="abs"
        label={labels.abs}
        intensity={getIntensity('abs')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('abs')}
        compact={compact}
      />

      {/* Obliques (sides of waist) */}
      <Muscle
        d="M 70 180 Q 65 215 72 248 L 88 245 L 88 185 Z"
        group="obliques"
        label={labels.obliques}
        intensity={getIntensity('obliques')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('obliques')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 130 180 Q 135 215 128 248 L 112 245 L 112 185 Z"
        group="obliques"
        label={labels.obliques}
        intensity={getIntensity('obliques')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('obliques')}
        variant="right"
        compact={compact}
      />

      {/* Quads (front of upper leg) */}
      <Muscle
        d="M 78 295 Q 72 350 74 405 Q 78 430 88 432 Q 92 410 92 360 L 92 295 Z"
        group="quads"
        label={labels.quads}
        intensity={getIntensity('quads')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('quads')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 122 295 Q 128 350 126 405 Q 122 430 112 432 Q 108 410 108 360 L 108 295 Z"
        group="quads"
        label={labels.quads}
        intensity={getIntensity('quads')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('quads')}
        variant="right"
        compact={compact}
      />

      {/* Calves (front lower leg) — small visible portion */}
      <Muscle
        d="M 76 440 Q 74 460 80 478 Q 88 482 90 470 Q 90 450 88 438 Z"
        group="calves"
        label={labels.calves}
        intensity={getIntensity('calves')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('calves')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 124 440 Q 126 460 120 478 Q 112 482 110 470 Q 110 450 112 438 Z"
        group="calves"
        label={labels.calves}
        intensity={getIntensity('calves')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('calves')}
        variant="right"
        compact={compact}
      />
    </g>
  );
}

// ===========================================================================
// Back view
// ===========================================================================
function BackView({ labels, getIntensity, hovered, setHovered, handle, compact }) {
  return (
    <g data-testid="muscle-map-back-regions">
      {/* Traps (upper back, diamond shape near neck) */}
      <Muscle
        d="M 85 75 Q 80 95 88 110 L 100 115 L 100 72 Q 92 70 85 75 Z"
        group="traps"
        label={labels.traps}
        intensity={getIntensity('traps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('traps')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 115 75 Q 120 95 112 110 L 100 115 L 100 72 Q 108 70 115 75 Z"
        group="traps"
        label={labels.traps}
        intensity={getIntensity('traps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('traps')}
        variant="right"
        compact={compact}
      />

      {/* Rear delts (back of shoulder) */}
      <Muscle
        d="M 60 95 Q 55 100 60 115 Q 70 118 80 112 L 80 92 Z"
        group="rear_delts"
        label={labels.rear_delts}
        intensity={getIntensity('rear_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('rear_delts')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 140 95 Q 145 100 140 115 Q 130 118 120 112 L 120 92 Z"
        group="rear_delts"
        label={labels.rear_delts}
        intensity={getIntensity('rear_delts')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('rear_delts')}
        variant="right"
        compact={compact}
      />

      {/* Lats (mid back wings) */}
      <Muscle
        d="M 78 120 Q 65 140 60 180 Q 68 200 80 195 L 95 175 L 95 120 Z"
        group="lats"
        label={labels.lats}
        intensity={getIntensity('lats')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('lats')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 122 120 Q 135 140 140 180 Q 132 200 120 195 L 105 175 L 105 120 Z"
        group="lats"
        label={labels.lats}
        intensity={getIntensity('lats')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('lats')}
        variant="right"
        compact={compact}
      />

      {/* Lower back (lumbar) */}
      <Muscle
        d="M 85 200 L 115 200 L 115 250 L 85 250 Z"
        group="lower_back"
        label={labels.lower_back}
        intensity={getIntensity('lower_back')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('lower_back')}
        compact={compact}
      />

      {/* Triceps (back of upper arm) */}
      <Muscle
        d="M 42 130 Q 38 160 45 195 Q 55 198 60 188 Q 60 160 60 135 Z"
        group="triceps"
        label={labels.triceps}
        intensity={getIntensity('triceps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('triceps')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 158 130 Q 162 160 155 195 Q 145 198 140 188 Q 140 160 140 135 Z"
        group="triceps"
        label={labels.triceps}
        intensity={getIntensity('triceps')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('triceps')}
        variant="right"
        compact={compact}
      />

      {/* Forearms (back, visible) */}
      <Muscle
        d="M 44 200 Q 42 215 48 222 Q 58 220 60 210 Q 60 200 58 196 Z"
        group="forearms"
        label={labels.forearms}
        intensity={getIntensity('forearms')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('forearms')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 156 200 Q 158 215 152 222 Q 142 220 140 210 Q 140 200 142 196 Z"
        group="forearms"
        label={labels.forearms}
        intensity={getIntensity('forearms')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('forearms')}
        variant="right"
        compact={compact}
      />

      {/* Glutes */}
      <Muscle
        d="M 70 252 Q 65 280 75 295 L 95 295 L 95 252 Z"
        group="glutes"
        label={labels.glutes}
        intensity={getIntensity('glutes')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('glutes')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 130 252 Q 135 280 125 295 L 105 295 L 105 252 Z"
        group="glutes"
        label={labels.glutes}
        intensity={getIntensity('glutes')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('glutes')}
        variant="right"
        compact={compact}
      />

      {/* Hamstrings (back of upper leg) */}
      <Muscle
        d="M 78 295 Q 72 350 74 405 Q 78 430 88 432 Q 92 410 92 360 L 92 295 Z"
        group="hamstrings"
        label={labels.hamstrings}
        intensity={getIntensity('hamstrings')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('hamstrings')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 122 295 Q 128 350 126 405 Q 122 430 112 432 Q 108 410 108 360 L 108 295 Z"
        group="hamstrings"
        label={labels.hamstrings}
        intensity={getIntensity('hamstrings')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('hamstrings')}
        variant="right"
        compact={compact}
      />

      {/* Calves (back lower leg) */}
      <Muscle
        d="M 76 440 Q 72 460 80 482 Q 92 488 92 470 Q 90 450 88 438 Z"
        group="calves"
        label={labels.calves}
        intensity={getIntensity('calves')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('calves')}
        variant="left"
        compact={compact}
      />
      <Muscle
        d="M 124 440 Q 128 460 120 482 Q 108 488 108 470 Q 110 450 112 438 Z"
        group="calves"
        label={labels.calves}
        intensity={getIntensity('calves')}
        hovered={hovered}
        onHover={setHovered}
        onClick={handle('calves')}
        variant="right"
        compact={compact}
      />
    </g>
  );
}

// ===========================================================================
// Muscle — single muscle region path. Colour-coded by intensity through
// a 4-stop heat gradient so the eye can read heavy vs light quickly.
// ===========================================================================
function Muscle({
  d,
  group,
  label,
  intensity,
  hovered,
  onHover,
  onClick,
  variant,
  compact,
}) {
  const { fill, filter } = intensityColor(intensity, hovered === group);

  return (
    <g
      data-testid={`muscle-${group}-${variant || 'main'}`}
      onMouseEnter={() => onHover(group)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <path
        d={d}
        fill={fill}
        stroke={hovered === group ? 'rgba(255, 235, 130, 0.95)' : 'rgba(255,255,255,0.05)'}
        strokeWidth={hovered === group ? 1.6 : 0.8}
        filter={filter}
        style={{
          transformOrigin: 'center',
          transformBox: 'fill-box',
          transform: hovered === group ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.15s ease, stroke 0.15s ease, stroke-width 0.15s ease',
        }}
        aria-label={label}
      >
        <title>{label}</title>
      </path>
    </g>
  );
}

/**
 * intensityColor — 4-stop heat gradient keyed off intensity (0..1).
 *
 *   0.0  → neutral dark fill, no glow
 *   0.1  → soft amber (warm-up territory)
 *   0.4  → orange (moderate)
 *   0.7  → red-orange (heavy)
 *   1.0  → inferno yellow (max), with stronger glow filter
 *
 * Returns { fill, filter } so the JSX stays simple. The hex stops are
 * tuned for the dark UI shell (primary-200 ≈ #d8ff85, primary-300 ≈
 * #d5ff7b); inferno is shifted toward warm gold/red so it doesn't
 * clash with the rest of the lime accent system.
 */
function intensityColor(intensity, isHovered) {
  if (intensity <= 0) {
    return { fill: 'rgba(255, 255, 255, 0.04)', filter: undefined };
  }
  // 4-stop heat gradient: amber → orange → red-orange → inferno yellow
  const stops = [
    { t: 0.0, c: [255, 196, 100] },  // amber
    { t: 0.4, c: [255, 140, 70] },   // orange
    { t: 0.7, c: [255, 95, 80] },    // red-orange
    { t: 1.0, c: [255, 230, 90] },   // inferno yellow
  ];
  // Find the two stops we sit between
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (intensity >= stops[i].t && intensity <= stops[i + 1].t) {
      lo = stops[i];
      hi = stops[i + 1];
      break;
    }
  }
  const span = hi.t - lo.t || 1;
  const localT = (intensity - lo.t) / span;
  const r = Math.round(lo.c[0] + (hi.c[0] - lo.c[0]) * localT);
  const g = Math.round(lo.c[1] + (hi.c[1] - lo.c[1]) * localT);
  const b = Math.round(lo.c[2] + (hi.c[2] - lo.c[2]) * localT);
  // Slight boost on hover so the user feels the affordance
  const alpha = isHovered ? 0.98 : 0.88;
  const filter = intensity >= 0.7
    ? 'url(#muscle-glow-hot)'
    : intensity >= 0.4
      ? 'url(#muscle-glow)'
      : undefined;
  return {
    fill: `rgba(${r}, ${g}, ${b}, ${alpha})`,
    filter,
  };
}

// ===========================================================================
// View toggle
// ===========================================================================
function ViewToggle({ view, setView, t }) {
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
      role="tablist"
    >
      <button
        type="button"
        onClick={() => setView('front')}
        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
          view === 'front'
            ? 'bg-primary-300 text-[#0a1208]'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
        }`}
        data-testid="muscle-view-front"
      >
        {t('muscleMap.viewFront', { defaultValue: 'Front' })}
      </button>
      <button
        type="button"
        onClick={() => setView('back')}
        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
          view === 'back'
            ? 'bg-primary-300 text-[#0a1208]'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
        }`}
        data-testid="muscle-view-back"
      >
        {t('muscleMap.viewBack', { defaultValue: 'Back' })}
      </button>
    </div>
  );
}

// ===========================================================================
// Muscle list (sidebar) — intensity bar + label + sets/volume
// ===========================================================================
function MuscleList({ groups, labels, lookup, hovered, setHovered, onClick, t }) {
  return (
    <div className="space-y-1.5" data-testid="muscle-list">
      {groups.map((key) => {
        const stats = lookup?.[key];
        const intensity = stats?.intensity ?? 0;
        const isHovered = hovered === key;
        const noActivity = intensity === 0;
        return (
          <button
            key={key}
            type="button"
            onMouseEnter={() => setHovered(key)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onClick?.(key, stats)}
            className={`w-full text-left flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl border transition-colors ${
              isHovered
                ? 'border-[rgba(255,200,90,0.4)] bg-[rgba(255,200,90,0.06)]'
                : 'border-[var(--color-border)] bg-white/[0.02] hover:bg-white/[0.04]'
            }`}
            data-testid={`muscle-row-${key}`}
          >
            <IntensityBar intensity={intensity} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[var(--color-text)]">
                  {labels[key]}
                </span>
                {noActivity && (
                  <span className="text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
                    {t('muscleMap.notTrained', { defaultValue: 'Not trained' })}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-[var(--color-text-muted)] truncate">
                {stats?.exercises?.length > 0
                  ? stats.exercises.slice(0, 2).join(' · ') + (stats.exercises.length > 2 ? '…' : '')
                  : t('muscleMap.noExercises', { defaultValue: 'No exercises logged' })}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-xs text-[var(--color-text)]">
                {Math.round(intensity * 100)}%
              </div>
              {stats?.sets > 0 && (
                <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
                  {stats.sets} {t('muscleMap.sets', { defaultValue: 'sets' })}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function IntensityBar({ intensity }) {
  // Same 4-stop heat gradient as the body fill
  const { fill } = intensityColor(intensity, false);
  return (
    <div className="w-1 h-8 rounded-full bg-white/[0.04] border border-[var(--color-border)] overflow-hidden relative">
      <div
        className="absolute bottom-0 left-0 right-0 rounded-full"
        style={{
          height: `${Math.max(intensity * 100, intensity > 0 ? 8 : 0)}%`,
          background: fill,
        }}
      />
    </div>
  );
}

// ===========================================================================
// Cardio stat — separate card for the cardio bucket
//
// Activity check: cardio volume is almost always 0 (bodyweight +
// reps — _parse_weight returns 0 for "bodyweight") so we check sets
// and exercise count instead. A 30-min run at "bodyweight" 30 reps
// counts as activity even though the kg-volume is 0.
// ===========================================================================
function CardioStat({ stats, t }) {
  const volume = stats?.volume ?? 0;
  const sets = stats?.sets ?? 0;
  const exercises = stats?.exercises ?? [];
  const intensity = stats?.intensity ?? 0;
  const hasActivity = sets > 0 || exercises.length > 0;
  const { fill } = intensityColor(intensity, false);
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-white/[0.02] p-3"
      data-testid="muscle-cardio"
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-[var(--color-border)]"
          style={{ background: hasActivity ? fill : 'rgba(255,255,255,0.04)' }}
        >
          <Heart className="w-4 h-4 text-[#0a1208]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--color-text)]">
            {t('muscleMap.cardio', { defaultValue: 'Cardio & Full body' })}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {hasActivity
              ? t('muscleMap.cardioActive', {
                  defaultValue: 'Running, cycling, rowing, full-body',
                })
              : t('muscleMap.cardioEmpty', { defaultValue: 'Not logged this period' })}
          </div>
        </div>
        {hasActivity && (
          <div className="text-right shrink-0">
            <div className="font-display text-sm text-[var(--color-text)]">
              {Math.round(sets)} {t('muscleMap.sets', { defaultValue: 'sets' })}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {exercises.length} {t('muscleMap.cardioSessions', { defaultValue: 'sessions' })}
            </div>
          </div>
        )}
      </div>
      {hasActivity && exercises.length > 0 && (
        <p className="text-xs text-[var(--color-text-muted)] truncate">
          {exercises.slice(0, 3).join(' · ')}
          {exercises.length > 3 ? '…' : ''}
        </p>
      )}
    </div>
  );
}

// ===========================================================================
// Color scale legend — 4-stop heat gradient (amber → orange → red-orange
// → inferno yellow) matches the muscle fill so users can read the
// encoding.
// ===========================================================================
function ColorScale({ t }) {
  return (
    <div className="pt-2 border-t border-[var(--color-border)]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-2">
        <Flame className="w-3 h-3 text-primary-200" />
        {t('muscleMap.intensity', { defaultValue: 'Intensity' })}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[var(--color-text-muted)]">0</span>
        <div
          className="flex-1 h-2 rounded-full"
          style={{
            background:
              'linear-gradient(to right, rgba(255,196,100,0.15), rgba(255,196,100,0.7), rgba(255,140,70,0.95), rgba(255,95,80,0.98), rgba(255,230,90,1))',
          }}
        />
        <span className="text-[10px] text-[var(--color-text-muted)]">100</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]">
        <Sparkles className="w-3 h-3 text-primary-100" />
        {t('muscleMap.glowHint', { defaultValue: 'Hotter regions glow more' })}
      </div>
    </div>
  );
}

export default MuscleMap;
